
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import * as echarts from 'echarts/core';
import {
  TooltipComponent,
  TooltipComponentOption,
  GridComponent,
  GridComponentOption,
  LegendComponent,
  LegendComponentOption,
  MarkLineComponent,
  MarkLineComponentOption,
  MarkPointComponent,
  MarkPointComponentOption,
} from 'echarts/components';
import { LineChart, LineSeriesOption } from 'echarts/charts';
import { UniversalTransition } from 'echarts/features';
import { CanvasRenderer } from 'echarts/renderers';
import { getHomeStockPrice, getHomeOneYearReport } from '@/apis/index';
import { AJAX_STATUS } from '@/config/config';
import chartstart from '@/assets/images/report-chart-start.png';
import CommonRankNumber from '@/components/common/CommonRankNumber.vue';
import CommonLoading from '@/components/common/CommonLoading.vue';
import { jumpReport, jumpTimeShare, debounce, formateHkStockcode } from '@/utils/tools';

echarts.use([
  TooltipComponent,
  GridComponent,
  LegendComponent,
  MarkLineComponent,
  MarkPointComponent,
  LineChart,
  CanvasRenderer,
  UniversalTransition,
]);

type EChartsOption = echarts.ComposeOption<
  | TooltipComponentOption
  | GridComponentOption
  | LegendComponentOption
  | MarkLineComponentOption
  | MarkPointComponentOption
  | LineSeriesOption
>;

type stockInfoType = {
  stockName: string;
  stockCode: string;
  orgName: string;
  orgId: string;
  author: string;
  authorId: string;
  code: string;
  organization: string;
  organizationId: string;
  market: string;
  pid: string;
};
@Component({
  components: {
    CommonRankNumber,
    CommonLoading,
  },
})
export default class CommonReportChart extends Vue {
  // 传递的是展示的数组
  @Prop() private isFirst!: boolean;
  @Prop() private stockInfo!: stockInfoType;
  @Prop() private stat!: string;
  @Prop() private priceData!: any;
  @Watch('stockInfo', { immediate: true, deep: true })
  onStockInfoChanged(newVal) {
    if (newVal.stockCode && !this.isDone) {
      this.$nextTick(() => {
        this.handleChartInit();
      });
    } else {
      this.ajaxStatus = AJAX_STATUS.nodata;
      this.clearChart();
    }
  }
  isShowChart = false;
  ajaxStatus = AJAX_STATUS.loading;
  lineDate: any = '';
  lineInfo: any = {
    target: '',
    price: '',
  };
  dateList = [];
  priceList = [];
  markLineInfo = [];
  reportLineInfo = [];
  starInfo = [];
  pointInfo = [];
  myChart: any = null;
  isDone = false;
  finalIndex = 0;
  maxPrice = 0;
  scrollEvent: any = null;
  touchIndex = 0;
  get isDarkColor() {
    return this.$isBlack ? '255,255,255' : '0,0,0';
  }
  get stockCode() {
    return formateHkStockcode(this.stockInfo.stockCode, this.stockInfo.market);
  }
  selectReport() {
    // 触发更新
    this.myChart &&
      this.myChart.dispatchAction({
        type: 'updateAxisPointer',
        currTrigger: 'mousemove',
        dataIndex: this.finalIndex,
        seriesIndex: 0,
      });
  }
  toReport() {
    this.stat && window.hxmClickStat(`${this.stat}.chakan`);
    // 跳转到报告
    jumpReport(this.reportLineInfo[this.finalIndex].pid);
  }
  toFenshi() {
    this.stat && window.hxmClickStat(`${this.stat}.ggfs`);
    const { stockCode, market } = this.stockInfo;
    jumpTimeShare({
      stockCode,
      market,
    });
  }
  handleChartInit() {
    const chart = this.$refs.reportchart as HTMLElement;
    if (!chart) {
      return;
    }
    const dom = chart.parentElement.parentElement.parentElement;
    // 获取滚动条滚动的高度
    const totalHeight =
      window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight;
    // 当滚动条滚动时，top, left, bottom, right时刻会发生改变。
    const { top, bottom } = dom.getBoundingClientRect();
    // 有部分在头部
    const isTop = top >= 0 && top <= totalHeight;
    // 有部分在底部
    const isBottom = bottom >= 0 && bottom <= totalHeight;
    if (isTop || isBottom) {
      // 处理在视角内
      this.handleInView();
    } else {
      // 如果已经初始化并且图表已经离开了视窗 清除图表
      if (this.myChart && (top > totalHeight || bottom < 0)) {
        !this.myChart.isDisposed() && this.myChart.dispose();
      }
    }
  }
  handleInView() {
    if (!this.isDone) {
      // 如果没有获取过接口 获取接口
      this.isDone = true;
      this.getData();
    } else {
      if (this.myChart) {
        // 已经消除过图表实例 初始化图表
        this.myChart.isDisposed() && this.initChart();
      }
    }
  }
  handleScroll() {
    // 监听滚动 处理图表显示
    window.addEventListener('scroll', this.scrollEvent);
  }
  // eslint-disable-next-line max-lines-per-function
  initChart() {
    const chartDom = this.$refs.reportchart as HTMLElement;
    if (!this.isShowChart || !chartDom.clientHeight) {
      return;
    }
    if (!this.myChart || (this.myChart && this.myChart.isDisposed())) {
      this.myChart = echarts.init(chartDom, null, { renderer: 'svg' });
    }
    const dpr = this.$dpr;
    const that = this;
    const option: EChartsOption = {
      tooltip: {
        trigger: 'axis',
        axisPointer: {},
        showContent: false,
      },
      grid: {
        top: '10%',
        bottom: '14%',
        right: '3%',
        left: '3%',
      },
      xAxis: {
        type: 'category',
        data: this.dateList,
        axisLine: {
          show: false,
        },
        axisTick: {
          show: false,
        },
        axisLabel: {
          show: false,
        },
        boundaryGap: true,
        axisPointer: {
          type: 'line',
          animation: false,
          z: 2,
          lineStyle: {
            color: `rgba(${this.isDarkColor},0.6)`,
            width: 1 * dpr,
            type: 'solid',
          },
          label: {
            show: false,
            formatter(params) {
              that.lineDate = params.value;
              that.lineInfo.price = params.seriesData[0].data;
              that.lineInfo.target = that.reportLineInfo[params.seriesData[0].dataIndex]?.targetPrice;
              that.touchIndex = params.seriesData[0].dataIndex;
              return '';
            },
          },
        },
      },
      yAxis: {
        type: 'value',
        axisLabel: {
          show: false,
        },
        axisLine: {
          show: false,
        },
        axisTick: {
          show: false,
        },
        splitLine: {
          show: false,
        },
        min(value) {
          return value.min * 0.8;
        },
        max(value) {
          return Math.max(that.maxPrice, value.max) * 1.2;
        },
      },
      series: [
        {
          type: 'line',
          name: 'priceline',
          data: this.priceList,
          showSymbol: false,
          symbolSize: 0,
          emphasis: {
            disabled: true,
            itemStyle: {
              color: 'transparent',
              borderColor: 'transparent',
              borderWidth: 0,
            },
          },
          lineStyle: {
            width: 1.5 * dpr,
            color: '#858585',
          },
          markLine: {
            data: this.markLineInfo,
            lineStyle: {
              type: [4 * dpr, 4 * dpr],
              width: 1 * dpr,
            },
            emphasis: {
              itemStyle: {
                type: [4 * dpr, 4 * dpr],
                width: 1 * dpr,
              },
            },
          },
          markPoint: {
            data: this.pointInfo,
          },
        },
      ],
    };
    setTimeout(() => {
      this.myChart.setOption(option);
      this.selectReport();
      this.myChart.getZr().on('mouseup', () => {
        const sortArr = this.starInfo.map(item => Math.abs(item - this.touchIndex));
        const min = Math.min(...sortArr);
        this.finalIndex = this.starInfo[sortArr.indexOf(min)];
        this.selectReport();
      });
      this.myChart.getZr().on('click', this.selectReport);
    }, 100);
  }
  getReport(tempObj: object, priceList: any[]) {
    getHomeOneYearReport({
      stock_code: this.stockInfo.stockCode.replace('HK', '0'),
      ...tempObj,
    }).then(report => {
      if (report.statusCode === 0 && report.data.list) {
        this.handleReport(priceList, report);
        setTimeout(() => {
          this.initChart();
        }, 100);
      }
    });
  }
  handleReport(priceList, report) {
    const dpr = this.$dpr;
    const colorArr = [
      { name: '无', color: '#FF9500' },
      { name: '买入', color: '#ff2436' },
      { name: '增持', color: '#FF661A' },
      { name: '中性', color: '#FF9500' },
      { name: '减持', color: '#39BC6F' },
      { name: '卖出', color: '#07AB4B' },
    ];
    report.data.list.forEach(item => {
      // 格式化时间
      const reportDate = item.date.replace(/^(\d{4})(\d{2})(\d{2})$/, '$1-$2-$3');
      // 研报日期在股价全量数据中的索引
      let reportIndex = this.dateList.indexOf(reportDate);
      if (reportIndex === -1) {
        // 没有找到需要将研报数据导入到索引中
        this.dateList.push(reportDate);
        this.dateList.sort();
        reportIndex = this.dateList.indexOf(reportDate);
        if (reportIndex) {
          priceList.splice(reportIndex, 0, priceList[reportIndex - 1]);
        } else {
          priceList.unshift(this.priceList[0]);
        }
      }
      if (!item.rate) {
        return;
      }
      // 图表数据中必须包含当前研报的信息
      if (`${item.pid}` === `${this.stockInfo.pid}`) {
        // 默认定位到当前研报的位置
        this.finalIndex = reportIndex;
        // 是否展示图表为true
        this.isShowChart = true;
      } else {
        // 清除掉滚动监听
        this.clearChart();
        if (this.starInfo.includes(reportIndex)) {
          return;
        }
      }
      this.starInfo.push(reportIndex);
      const targetPrice = parseFloat((item.targetPrice / 100).toFixed(2));
      const price = priceList[reportIndex];
      const pointColor = colorArr[item.rate].color;
      item.targetPrice = targetPrice;
      this.reportLineInfo[reportIndex] = item;
      this.maxPrice = Math.max(targetPrice, this.maxPrice);
      // label标签的样式对象
      const label = {
        show: true,
        color: '#fff',
        formatter: colorArr[item.rate].name,
        height: 14 * dpr,
        lineHeight: 14 * dpr,
        width: 26 * dpr,
        backgroundColor: pointColor,
        fontSize: 11 * dpr,
        borderRadius: 2 * dpr,
      };
      // 如果有目标价 / 没有目标价的时候 有目标价不展示标记点的label 没有的时候展示label
      const pointLabel = targetPrice ? {} : { ...label, position: 'top' };
      this.pointInfo.push({
        symbol: 'circle',
        symbolSize: 5 * dpr,
        itemStyle: {
          color: pointColor,
        },
        coord: [reportIndex, price],
        emphasis: {
          itemStyle: {
            color: '#fff',
            borderColor: pointColor,
            borderWidth: dpr,
          },
          scale: false,
          label: pointLabel,
        },
      });
      item.targetPrice &&
        this.markLineInfo.push([
          {
            coord: [reportIndex, priceList[reportIndex]],
            symbol: 'none',
            lineStyle: { color: `rgba(${this.isDarkColor},0.24)` },
          },
          {
            coord: [reportIndex, targetPrice],
            symbol: `image://${chartstart}`,
            symbolSize: 12 * dpr,
            label: { show: false },
            emphasis: {
              label,
            },
          },
        ]);
    });
  }
  handlePriceData(list: any) {
    const { nodata } = AJAX_STATUS;
    const dateList = [];
    this.ajaxStatus = nodata;
    const priceList = [];
    if (list?.length) {
      // 存在数据进行数据处理
      list.forEach(item => {
        dateList.push(item.date.replace(/^(\d{4})(\d{2})(\d{2})$/, '$1-$2-$3'));
        priceList.push(item.price);
        this.reportLineInfo.push(null);
      });
      // 日期列表
      this.dateList = dateList;
      // 价格列表
      this.priceList = priceList;
      this.lineInfo.price = priceList[priceList.length - 1];
      this.lineDate = dateList[dateList.length - 1];
      type ajax = {
        author_id?: string;
        org_id?: string;
      };
      // 获取研报列表接口需要传递的数据处理
      let tempObj: ajax = {};
      if (this.stockInfo.authorId) {
        // 有作者传作者id 无作者传机构id 无机构不传
        tempObj.author_id = this.stockInfo.authorId;
      } else if (this.stockInfo.orgId || this.stockInfo.organizationId) {
        tempObj.org_id = this.stockInfo.orgId || this.stockInfo.organizationId;
      } else {
        tempObj = {};
      }
      this.getReport(tempObj, priceList);
    }
  }
  getData() {
    if (this.priceData) {
      this.handlePriceData(this.priceData);
    } else {
      const { nodata, loading, end } = AJAX_STATUS;
      const { stockCode } = this.stockInfo;
      if (!stockCode || stockCode.includes('!')) {
        this.ajaxStatus = nodata;
        return;
      }
      this.ajaxStatus = loading;
      getHomeStockPrice({
        stock_code: stockCode.replace('HK', '0'),
      })
        .then(res => {
          this.handlePriceData(res.data?.list);
        })
        .catch(() => {
          this.ajaxStatus = end;
        });
    }
  }
  clearChart() {
    window.removeEventListener('scroll', this.scrollEvent, true);
  }
  mounted() {
    this.scrollEvent = debounce(this.handleChartInit, 200);
    this.handleScroll();
  }
  beforeDestory() {
    this.clearChart();
  }
}
